import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import { Badge } from "../../ui/badge";
import { supabase } from "../../../lib/supabase";
import LotteryNumlotWinningNumbers from "../LotteryNumlotWinningNumbers";
import LotteryNumlotIndexDetailTemplate from "./LotteryNumlotIndexDetailTemplate";

// ナンバーズの一致種別バッジ
const NM_MATCH_LABEL = {
  exact: "完全一致",
  box: "並べ替え一致",
  one_off: "1文字違い",
};

// ニア検索RPCの行を、トリガー表示用の共通形に正規化
function normalizeRow(type, row) {
  if (type === "loto") {
    return {
      round: row.round,
      draw_date: row.draw_date,
      mainNumber: (row.nums || []).join(","),
      bonusNumber: (row.bonus || []).join(","),
      badge: `本数字${row.match_count}個一致${row.bonus_match > 0 ? "＋ボーナス" : ""}`,
    };
  }
  if (type === "numbers") {
    return {
      round: row.round,
      draw_date: row.draw_date,
      mainNumber: row.num,
      bonusNumber: null,
      badge: NM_MATCH_LABEL[row.match_kind] || row.match_kind,
    };
  }
  // bingo
  return {
    round: row.round,
    draw_date: row.draw_date,
    mainNumber: (row.cells || []).join(","),
    bonusNumber: null,
    badge: `位置${row.pos_match_count}マス一致`,
  };
}

export default function LotteryNumlotDetailNearTemplate({ related, config }) {
  const [openRounds, setOpenRounds] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [loadingRounds, setLoadingRounds] = useState({});

  const lotteryKey = config?.key;

  // アコーディオンを開いたら、その回の詳細（払戻等）を遅延取得
  const handleValueChange = async (values) => {
    const newRound = values.find((v) => !openRounds.includes(v) && !detailCache[v]);
    setOpenRounds(values);
    if (!newRound) return;

    setLoadingRounds((prev) => ({ ...prev, [newRound]: true }));
    try {
      const { data, error } = await supabase
        .schema("chatorahack")
        .rpc("get_lottery_numlot_detail", {
          p_lottery_key: lotteryKey,
          p_round: parseInt(newRound, 10),
        });
      if (error) throw error;
      const row = data[0];
      if (!row) throw new Error("データなし");
      setDetailCache((prev) => ({ ...prev, [newRound]: { ...row, lotteryKey } }));
    } catch (err) {
      setDetailCache((prev) => ({ ...prev, [newRound]: null }));
    } finally {
      setLoadingRounds((prev) => ({ ...prev, [newRound]: false }));
    }
  };

  if (!related?.rows?.length) return null;
  const visibleRows = related.rows
    .filter((r) => r.round > 0)
    .map((r) => normalizeRow(related.type, r));
  if (visibleRows.length === 0) return null;

  return (
    <>
      <h2 className="mt-16 mb-1 pb-2 border-b border-primary-500 text-lg font-bold text-primary-500">
        出目が似ている過去の抽選
      </h2>
      <p className="mb-4 text-[10px] sm:text-xs text-primary-500/60">
        当選数字の並びが似ている回を、似ている順に最大5件表示しています。
      </p>

      <Accordion
        type="multiple"
        value={openRounds}
        onValueChange={handleValueChange}
        className="grid gap-4 pt-2"
      >
        {visibleRows.map((row) => (
            <AccordionItem
              key={row.round}
              value={String(row.round)}
              className="relative border border-primary-500 rounded-lg bg-base-50 w-full"
            >
              <div className="absolute -top-2 right-2 flex gap-1">
                <Badge
                  variant="outline"
                  className="bg-base-50 text-primary-500 border-primary-500 whitespace-nowrap"
                >
                  {row.badge}
                </Badge>
              </div>

              <AccordionTrigger className="flex flex-col md:flex-row items-center px-4 py-4 gap-2 relative hover:no-underline [&>svg]:ml-auto [&>svg]:shrink-0 [&>svg]:absolute [&>svg]:right-2 [&>svg]:md:relative [&>svg]:text-primary-500">
                <div className="flex flex-row md:flex-col items-baseline w-full md:w-auto gap-2 md:gap-0">
                  <span className="text-xs text-primary-500 font-bold">第 {row.round} 回</span>
                  <span className="text-sm text-primary-500">{row.draw_date}</span>
                </div>
                <div className="flex-1 flex justify-center w-full md:w-auto">
                  <LotteryNumlotWinningNumbers
                    mainNumber={row.mainNumber}
                    bonusNumber={row.bonusNumber}
                    config={config}
                    size="sm"
                  />
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-4 pb-4">
                <LotteryNumlotIndexDetailTemplate
                  data={detailCache[String(row.round)]}
                  config={config}
                  loading={loadingRounds[String(row.round)] ?? false}
                  showShare={false}
                />
              </AccordionContent>
            </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-4 text-[10px] text-primary-500/50 text-right">
        ※数字の近さは参考情報です。当選を保証するものではありません。
      </p>
    </>
  );
}
